import * as THREE from 'three';
import type { Pos } from '@color-dots/game';
import { projectOntoSphere } from './arcCurve3d.js';
import { gridToWorld } from './Grid3DLayout.js';

/** Sphere center/radius used to project pickable cells onto the glass sphere's surface, matching the visuals. */
export interface SphereBounds {
  center: THREE.Vector3;
  radius: number;
  lift?: number;
}

export class RaycastPicker {
  private worldPos = new THREE.Vector3();
  private projected = new THREE.Vector3();

  pick(
    clientX: number,
    clientY: number,
    camera: THREE.PerspectiveCamera,
    boardGroup: THREE.Group,
    size: number,
    domElement: HTMLElement,
    sphereBounds?: SphereBounds,
  ): Pos | null {
    const rect = domElement.getBoundingClientRect();
    const clickX = clientX - rect.left;
    const clickY = clientY - rect.top;

    let bestCol = -1;
    let bestRow = -1;
    let bestDistSq = Infinity;

    for (let row = 0; row < size; row++) {
      for (let col = 0; col < size; col++) {
        this.worldPos.copy(gridToWorld(col, row, size));
        if (sphereBounds && sphereBounds.radius > 1e-6) {
          this.worldPos.copy(
            projectOntoSphere(this.worldPos, sphereBounds.center, sphereBounds.radius, sphereBounds.lift ?? 0),
          );
        }
        boardGroup.localToWorld(this.worldPos);
        this.projected.copy(this.worldPos).project(camera);

        if (this.projected.z > 1) continue;

        const sx = (this.projected.x * 0.5 + 0.5) * rect.width;
        const sy = (-this.projected.y * 0.5 + 0.5) * rect.height;
        const distSq = (sx - clickX) ** 2 + (sy - clickY) ** 2;

        if (distSq < bestDistSq) {
          bestDistSq = distSq;
          bestCol = col;
          bestRow = row;
        }
      }
    }

    if (bestCol < 0) return null;

    const maxDist = (Math.min(rect.width, rect.height) / size) * 0.6;
    if (bestDistSq > maxDist * maxDist) return null;

    return [bestCol, bestRow];
  }

  getHitPoint(
    clientX: number,
    clientY: number,
    camera: THREE.PerspectiveCamera,
    boardGroup: THREE.Group,
    size: number,
    domElement: HTMLElement,
    sphereBounds?: SphereBounds,
  ): THREE.Vector3 | null {
    const pos = this.pick(clientX, clientY, camera, boardGroup, size, domElement, sphereBounds);
    if (!pos) return null;

    const point = gridToWorld(pos[0], pos[1], size);
    if (sphereBounds && sphereBounds.radius > 1e-6) {
      return boardGroup.localToWorld(
        projectOntoSphere(point, sphereBounds.center, sphereBounds.radius, sphereBounds.lift ?? 0),
      );
    }
    boardGroup.localToWorld(point);
    return point;
  }
}
